/**
 * usePlacementSnapshot - TanStack Query hooks for the Ausschreibungs-Screen Tab 1 (Risikoprofil
 * snapshot) + Tab 2 (Soll-Deckungskatalog) (P2 Placement Slice 5).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*; mock mode resolves
 * lightweight fixtures so the two tabs render standalone (no backend). The server owns every
 * decision (materialization, gating, E12) - the UI only triggers + renders. Every mutation
 * invalidates the two snapshot keys + the placement detail + the customer list (status may advance).
 *
 * [Source: apps/web/src/features/placements/usePlacements.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { PLACEMENT_SNAPSHOT_SCHEMA_VERSION } from '@brokly/shared';
import type {
  PlacementBranchIstPatchBody,
  PlacementRisikoprofilResponse,
  PlacementSollDeckungResponse,
  Step2AnswerPutBody,
} from '@brokly/shared';

// ── Mock fixtures (mock mode only) ──────────────────────────────────────────────

function mockRisikoprofil(id: string): PlacementRisikoprofilResponse {
  const now = new Date().toISOString();
  return {
    placement_id: id,
    placement_number: 'PL-2026-000000',
    status: 'entwurf',
    editable: true,
    policy_id: null,
    branchencode: '60000',
    scoped_object_ref: null,
    general: {
      version: PLACEMENT_SNAPSHOT_SCHEMA_VERSION,
      profile_type: 'firma',
      attributes: {},
      felder: [],
      data_quality_score: null,
      last_reviewed_at: null,
      stammdaten_mirror: null,
      basissparten: [],
      profile_updated_at: now,
      snapshot_id: 'mock-g',
      created_at: now,
      updated_at: now,
      consent_gated_fields_suppressed: [],
      gated_fields_suppressed_on_read: [],
      consent_present_at_snapshot: false,
      consent_present_now: false,
      filled: 0,
      total: 0,
    },
    general_hidden_reason: null,
    branch: {
      version: PLACEMENT_SNAPSHOT_SCHEMA_VERSION,
      branchencode: '60000',
      felder: [],
      snapshot_id: 'mock-b',
      created_at: now,
      updated_at: now,
      objekte: [
        {
          insurance_object_ref: null,
          object_label: null,
          origin_attributes: {},
          attributes: {},
          origin_source: null,
          origin_policy_risk_attributes_id: null,
          updated_at: now,
          filled: 0,
          total: 0,
          changed_keys: [],
        },
      ],
    },
    branch_hidden_reason: null,
    schadenverlauf: { available: false, reason: 'neugeschaeft' },
  };
}

function mockSollDeckung(id: string): PlacementSollDeckungResponse {
  return {
    placement_id: id,
    status: 'entwurf',
    editable: true,
    branchencode: '60000',
    items: [],
    basissparten_hinweis: [],
    progress: { beantwortet: 0, total: 0 },
    pflichtdeckung: { warnflag: false, warn_at: null, warn_rule_keys: [], ack_at: null, ack_by: null, unbestaetigt_kritisch: [] },
    police_prefill_hidden_reason: null,
  };
}

// ── Queries ──────────────────────────────────────────────────────────────────────

export function usePlacementRisikoprofil(id: string) {
  const live = isLiveData();
  return useQuery<PlacementRisikoprofilResponse>({
    queryKey: qk.placementRisikoprofil(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getRisikoprofil(id) : Promise.resolve(mockRisikoprofil(id))),
    ...(live ? {} : { initialData: () => mockRisikoprofil(id), staleTime: Infinity }),
  });
}

export function usePlacementSollDeckung(id: string) {
  const live = isLiveData();
  return useQuery<PlacementSollDeckungResponse>({
    queryKey: qk.placementSollDeckung(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getSollDeckung(id) : Promise.resolve(mockSollDeckung(id))),
    ...(live ? {} : { initialData: () => mockSollDeckung(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

/** Invalidate the two snapshot keys + the placement detail + the customer list (status may advance). */
function useSnapshotInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'risikoprofil', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'soll-deckung', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'list'] });
  };
}

export function usePatchBranchIst(id: string) {
  const live = isLiveData();
  const invalidate = useSnapshotInvalidate(id);
  return useMutation({
    mutationFn: (body: PlacementBranchIstPatchBody) =>
      live ? apiClient.placements.patchBranchIst(id, body) : Promise.resolve(mockRisikoprofil(id)),
    onSuccess: invalidate,
  });
}

export function useRefreshGeneral(id: string) {
  const live = isLiveData();
  const invalidate = useSnapshotInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.refreshGeneral(id) : Promise.resolve(mockRisikoprofil(id))),
    onSuccess: invalidate,
  });
}

export function useAbschliessenRisikoprofil(id: string) {
  const live = isLiveData();
  const invalidate = useSnapshotInvalidate(id);
  return useMutation({
    mutationFn: () =>
      live ? apiClient.placements.abschliessenRisikoprofil(id) : Promise.resolve(undefined),
    onSuccess: invalidate,
  });
}

export function usePutAnswer(id: string) {
  const live = isLiveData();
  const invalidate = useSnapshotInvalidate(id);
  return useMutation({
    mutationFn: ({ ruleKey, body }: { ruleKey: string; body: Step2AnswerPutBody }) =>
      live ? apiClient.placements.putAnswer(id, ruleKey, body) : Promise.resolve(mockSollDeckung(id)),
    onSuccess: invalidate,
  });
}

export function useAbschliessenSollDeckung(id: string) {
  const live = isLiveData();
  const invalidate = useSnapshotInvalidate(id);
  return useMutation({
    mutationFn: () =>
      live ? apiClient.placements.abschliessenSollDeckung(id) : Promise.resolve(undefined),
    onSuccess: invalidate,
  });
}

export function usePflichtdeckungKenntnisnahme(id: string) {
  const live = isLiveData();
  const invalidate = useSnapshotInvalidate(id);
  return useMutation({
    mutationFn: () =>
      live ? apiClient.placements.pflichtdeckungKenntnisnahme(id) : Promise.resolve(mockSollDeckung(id)),
    onSuccess: invalidate,
  });
}
